import type { ReactNode } from 'react'
import { ArrowRight, type LucideIcon } from 'lucide-react'

export interface RentalDoorFact {
  term: string
  detail: ReactNode
}

export interface RentalDoorProps {
  id: 'host' | 'describe'
  icon: LucideIcon
  title: string
  summary: string
  facts: RentalDoorFact[]
  /** One muted line of live data; omitted when it is not known. */
  live?: ReactNode
  action: string
  onChoose: () => void
  children?: ReactNode
}

/**
 * One front-door card.
 *
 * The card is a `div`, not a button, because Door A carries an interactive
 * disclosure and a button cannot contain a button. The title and the action
 * text are one `<button>` whose `::after` stretches over the whole card, so
 * the card clicks as one target while the disclosure sits above it. Both doors
 * use the same pattern so they behave the same.
 */
export function RentalDoor({ id, icon: Icon, title, summary, facts, live, action, onChoose, children }: RentalDoorProps) {
  const titleId = `rental-door-${id}-title`
  const summaryId = `rental-door-${id}-summary`
  return (
    <div className="rental-door" data-door={id}>
      <span className="rental-door-icon" aria-hidden="true"><Icon size={18} /></span>
      <h2 className="rental-door-title" id={titleId}>{title}</h2>
      <p className="rental-door-summary" id={summaryId}>{summary}</p>
      <dl className="rental-door-facts">
        {facts.map((fact) => (
          <div key={fact.term} className="rental-door-fact">
            <dt>{fact.term}</dt>
            <dd>{fact.detail}</dd>
          </div>
        ))}
      </dl>
      {children}
      {live ? <p className="rental-door-live">{live}</p> : null}
      <button type="button" className="rental-door-action" aria-labelledby={titleId} aria-describedby={summaryId} onClick={onChoose}>
        <span>{action}</span>
        <ArrowRight size={16} aria-hidden="true" />
      </button>
    </div>
  )
}
